/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { defineMessages } from 'react-intl-next';
import { useState } from 'react';
import { Subscribe } from 'unstated';

import { jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { AIPlugin } from '@atlassian/editor-plugin-ai';

import { Flags } from '@confluence/flags/entry-points/Flags';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';

import { useEditorAIActionFlag } from './useEditorAIActionFlag';

export const i18n = defineMessages({
	title: {
		id: 'experiment-improve-writing-on-paste.improve-writing-flag.title',
		defaultMessage: 'Use AI by highlighting text',
		description: 'Popup title to bring awareness to the Improve Writing feature.',
	},
	description: {
		id: 'experiment-improve-writing-on-paste.improve-writing-flag.description',
		defaultMessage: 'Edit with AI by highlighting text on the page and selecting Improve writing.',
		description:
			'Context explaining that users can use AI to edit their writing by highlighting text and selecting the Improve writing option.',
	},
});

type PresetAPI = PublicPluginAPI<[AIPlugin]>;

interface ImproveWritingFlagContainerProps {
	api: PresetAPI;
}

interface ImproveWritingFlagProps {
	promptAction: PromptActionCompleted | null;
}

export type ActionType = 'insert' | 'replace' | 'discard' | 'second-paste';
export type ActionSource = 'preview-screen' | 'discard-screen' | 'auto-paste';

export interface PromptActionCompleted {
	actionSource: ActionSource;
	actionType: ActionType;
	prompt: string;
}

export const ImproveWritingFlag = ({ promptAction }: ImproveWritingFlagProps) => {
	const [flagShownFirstPaste, setFlagShownFirstPaste] = useState(false);
	const [flagShownSecondPaste, setFlagShownSecondPaste] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return (
		<Subscribe to={[FlagsStateContainer]}>
			{(flags: FlagsStateContainer) => {
				if (
					promptAction &&
					(!flagShownFirstPaste ||
						(!flagShownSecondPaste && promptAction.actionType === 'second-paste'))
				) {
					if (promptAction.actionType === 'second-paste') {
						setFlagShownSecondPaste(true);
					}
					// edge case: first paste occurs without a flag trigger (ex: navigate away before finishing experience)
					//   -> second paste causes infinite display of first paste flag
					// to prevent this, we always setFlagShownFirstPaste -> true
					setFlagShownFirstPaste(true);

					createAnalyticsEvent({
						type: 'sendScreenEvent',
						data: {
							action: 'viewed',
							name: 'improveWritingFlag',
							attributes: {
								actionSource: promptAction?.actionSource,
								actionType: promptAction?.actionType,
								prompt: promptAction?.prompt,
								source: 'improveWritingOnPaste',
							},
						},
					}).fire();

					void flags.showCustomFlag({
						id: 'improve-writing-flag',
						title: i18n.title.defaultMessage,
						description: i18n.description.defaultMessage,
						appearance: 'normal',
						customIcon: (
							<StatusInformationIcon label="Info" color={token('color.icon.information')} />
						),
						onClose: () => {
							createAnalyticsEvent({
								type: 'sendUIEvent',
								data: {
									action: 'clicked',
									actionSubject: 'button',
									actionSubjectId: 'improveWritingFlag',
									source: 'improveWritingOnPaste',
									attributes: {
										actionSource: promptAction?.actionSource,
										actionType: promptAction?.actionType,
										prompt: promptAction?.prompt,
										buttonType: 'dismissButton',
									},
								},
							}).fire();
						},
					});
				}

				return <Flags />;
			}}
		</Subscribe>
	);
};

export const ImproveWritingFlagContainer = ({ api }: ImproveWritingFlagContainerProps) => {
	const { promptActionCompleted } = useEditorAIActionFlag(api);

	return <ImproveWritingFlag promptAction={promptActionCompleted} />;
};
